import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';

const TYPE_COLORS = {
  company: 'hsl(var(--primary))',
  service: 'hsl(var(--success))',
  product: 'hsl(var(--warning))',
  leader: '#a855f7',
  competitor: 'hsl(var(--destructive))',
  social: '#0ea5e9',
};

export default function AuthorityGraph() {
  const { activeClient } = useClient();
  const [graph, setGraph] = useState(null);

  useEffect(() => {
    if (!activeClient) return;
    api.get(`/clients/${activeClient.client_id}/graph`).then(r => setGraph(r.data));
  }, [activeClient]);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;
  if (!graph) return <div className="text-sm text-muted-foreground">Loading graph…</div>;

  const W = 900, H = 560;
  const cx = W / 2, cy = H / 2;
  const others = graph.nodes.filter(n => n.id !== 'company');
  const radius = Math.min(W, H) / 2 - 60;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Authority Graph™</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Living knowledge graph</h1>
        <p className="mt-2 text-sm text-muted-foreground">Entities, products, leadership, competitors and social presence — connected.</p>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <defs>
            <radialGradient id="bg" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="hsl(var(--primary) / 0.06)" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#bg)" />

          {others.map((n, i) => {
            const a = (i / others.length) * Math.PI * 2;
            const x = cx + Math.cos(a) * radius;
            const y = cy + Math.sin(a) * radius;
            return (
              <g key={n.id}>
                <line x1={cx} y1={cy} x2={x} y2={y} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <circle cx={x} cy={y} r="9" fill={TYPE_COLORS[n.type] || '#888'} opacity="0.9" />
                <text x={x} y={y - 14} textAnchor="middle" fontSize="11" fill="hsl(var(--foreground))" className="font-medium">{n.label.slice(0, 18)}</text>
                <text x={x} y={y + 22} textAnchor="middle" fontSize="9" fill="hsl(var(--muted-foreground))">{n.type}</text>
              </g>
            );
          })}

          <circle cx={cx} cy={cy} r="34" fill="hsl(var(--primary))" />
          <text x={cx} y={cy + 4} textAnchor="middle" fontSize="13" fill="hsl(var(--primary-foreground))" fontWeight="700">
            {graph.nodes[0]?.label?.slice(0, 14) || 'Company'}
          </text>
        </svg>
      </div>

      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TYPE_COLORS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
            <span className="capitalize text-muted-foreground">{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
